import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import CustomerLayout from '../components/CustomerLayout';
import CollapsibleTable from '../components/CollapsibleTable';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Eye, Edit2, Copy, Check, RefreshCw } from 'lucide-react';
import confirm from '../utils/confirm';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerScreens = () => {
  const { t } = useTranslation();
  const [screens, setScreens] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [selectedScreenDevices, setSelectedScreenDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [refreshingScreen, setRefreshingScreen] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [formData, setFormData] = useState({
    screen_name: '',
    qr_text: t('customerScreens.defaultQrText'),
    qr_links: [],
    orientation: 'landscape'
  });

  useEffect(() => {
    fetchScreens();
  }, []);

  const fetchScreens = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/screens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // For each screen, fetch online device count
      const screensWithDeviceCount = await Promise.all(
        response.data.map(async (screen) => {
          try {
            const devicesResponse = await axios.get(`${API}/screens/${screen.id}/devices`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const onlineCount = devicesResponse.data.filter(d => d.is_online).length;
            return { ...screen, online_devices: onlineCount };
          } catch (error) {
            return { ...screen, online_devices: 0 };
          }
        })
      );
      
      setScreens(screensWithDeviceCount);
    } catch (error) {
      toast.error(t('customerScreens.errorLoadingScreens'));
    }
  };

  const fetchScreenDevices = async (screenId) => {
    setLoadingDevices(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/screens/${screenId}/devices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedScreenDevices(response.data);
      setShowDevicesModal(true);
    } catch (error) {
      toast.error(t('customerScreens.errorLoadingDevices'));
    } finally {
      setLoadingDevices(false);
    }
  };

  // Refresh a single screen
  const handleRefreshScreen = async (screenId) => {
    setRefreshingScreen(screenId);
    try {
      const token = getToken();
      await axios.post(`${API}/screens/${screenId}/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customerScreens.refreshSent') || 'Oppdateringssignal sendt til skjermen');
    } catch (error) {
      toast.error(t('customerScreens.refreshError') || 'Kunne ikke sende oppdatering');
    } finally {
      setTimeout(() => setRefreshingScreen(null), 1000);
    }
  };

  // Refresh all screens
  const handleRefreshAllScreens = async () => {
    setRefreshingAll(true);
    try {
      const token = getToken();
      const response = await axios.post(`${API}/screens/refresh-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Oppdateringssignal sendt til ${response.data.screen_count} skjermer`);
    } catch (error) {
      toast.error('Kunne ikke sende oppdatering til skjermene');
    } finally {
      setTimeout(() => setRefreshingAll(false), 1000);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      await axios.post(`${API}/screens`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customerScreens.screenCreated'));
      setShowModal(false);
      fetchScreens();
      setFormData({
        screen_name: '',
        qr_text: t('customerScreens.defaultQrText'),
        qr_links: [],
        orientation: 'landscape'
      });
    } catch (error) {
      toast.error(t('customerScreens.errorCreatingScreen'));
    }
  };

  const handleEdit = (screen) => {
    setEditingScreen(screen);
    setFormData({
      screen_name: screen.screen_name,
      orientation: screen.orientation,
      qr_text: screen.qr_text || t('customerScreens.defaultQrText'),
      qr_links: screen.qr_links || []
    });
    setShowModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      await axios.put(`${API}/screens/${editingScreen.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customerScreens.screenUpdated'));
      setShowModal(false);
      setEditingScreen(null);
      fetchScreens();
      setFormData({
        screen_name: '',
        qr_text: t('customerScreens.defaultQrText'),
        qr_links: [],
        orientation: 'landscape'
      });
    } catch (error) {
      console.error('Update screen error:', error);
      if (error.response?.status === 404) {
        toast.error(t('customerScreens.screenNotFound'));
        // Refresh the list to remove stale screens
        fetchScreens();
        setShowModal(false);
        setEditingScreen(null);
      } else {
        toast.error(t('customerScreens.errorUpdatingScreen'));
      }
    }
  };

  const copyToClipboard = (displayUrl) => {
    navigator.clipboard.writeText(displayUrl).then(() => {
      setCopiedId(displayUrl);
      toast.success(t('customerScreens.linkCopied'));
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      toast.error(t('customerScreens.errorCopyingLink'));
    });
  };

  const handleDelete = async (screenId) => {
    if (!await confirm.danger(t('customerScreens.confirmDeleteScreen'))) return;
    
    try {
      const token = getToken();
      await axios.delete(`${API}/screens/${screenId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customerScreens.screenDeleted'));
      fetchScreens();
    } catch (error) {
      toast.error(t('customerScreens.errorDeletingScreen'));
    }
  };

  return (
    <CustomerLayout>
      <div className="admin-header">
        <h1>{t('customerScreens.title')}</h1>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button 
            className="btn btn-secondary" 
            onClick={handleRefreshAllScreens}
            disabled={refreshingAll || screens.length === 0}
            title="Oppdater alle skjermer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: refreshingAll ? 'rgba(80, 227, 194, 0.2)' : undefined
            }}
          >
            <RefreshCw 
              size={18} 
              style={{ 
                animation: refreshingAll ? 'spin 1s linear infinite' : undefined
              }}
            />
            <span style={{ display: window.innerWidth < 768 ? 'none' : 'inline' }}>
              {refreshingAll ? 'Sender...' : 'Oppdater alle'}
            </span>
          </button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)} data-testid="add-screen-btn">
            <Plus size={20} /> {t('customerScreens.newScreen')}
          </button>
        </div>
      </div>

      <CollapsibleTable
        columns={[
          {
            key: 'screen_name',
            label: t('customerScreens.tableName'),
            minWidth: '120px'
          },
          {
            key: 'qr_code_id',
            label: t('customerScreens.tableDirectLink'),
            minWidth: '180px',
            render: (screen) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <code style={{ 
                  background: 'rgba(80, 227, 194, 0.1)', 
                  padding: '0.5rem 0.75rem', 
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  color: '#50E3C2',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '160px',
                  direction: 'rtl',
                  textAlign: 'left'
                }}>
                  {screen.display_url ? screen.display_url.split('/').pop() : screen.qr_code_id}
                </code>
                <button
                  className="btn btn-secondary"
                  onClick={() => copyToClipboard(screen.display_url)}
                  style={{ 
                    padding: '0.4rem 0.75rem',
                    minWidth: 'auto'
                  }}
                  title={t('customerScreens.copyLink')}
                >
                  {copiedId === screen.display_url ? (
                    <Check size={16} color="#50E3C2" />
                  ) : (
                    <Copy size={16} />
                  )}
                </button>
              </div>
            )
          },
          {
            key: 'orientation',
            label: t('customerScreens.tableOrientation'),
            minWidth: '100px',
            render: (screen) => screen.orientation === 'landscape' ? t('customerScreens.landscape') : t('customerScreens.portrait')
          },
          {
            key: 'online_devices',
            label: t('customerScreens.tableActiveScreens'),
            minWidth: '120px',
            render: (screen) => (
              <button
                onClick={() => fetchScreenDevices(screen.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(80, 227, 194, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <span style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: '700',
                  color: screen.online_devices > 0 ? '#50E3C2' : '#666'
                }}>
                  {screen.online_devices || 0}
                </span>
                <span style={{ 
                  fontSize: '0.75rem',
                  color: screen.online_devices > 0 ? '#50E3C2' : '#666',
                  fontWeight: '500'
                }}>
                  {screen.online_devices === 1 ? 'enhet' : 'enheter'}
                </span>
                <Eye size={16} style={{ opacity: 0.5 }} />
              </button>
            )
          },
          {
            key: 'is_active',
            label: t('customerScreens.tableStatus'),
            minWidth: '90px',
            render: (screen) => (
              <span className={screen.is_active ? 'badge badge-success' : 'badge badge-danger'}>
                {screen.is_active ? t('customerScreens.active') : t('customerScreens.inactive')}
              </span>
            )
          }
        ]}
        data={screens}
        actions={(screen) => (
          <>
            <button
              className="btn btn-secondary"
              onClick={() => handleRefreshScreen(screen.id)}
              disabled={refreshingScreen === screen.id}
              data-testid={`refresh-screen-${screen.id}`}
              title={t('customerScreens.refreshScreen') || 'Oppdater skjerm'}
              style={{ 
                padding: 'clamp(0.4rem, 1vw, 0.5rem)',
                background: refreshingScreen === screen.id ? 'rgba(80, 227, 194, 0.2)' : undefined
              }}
            >
              <RefreshCw 
                size={window.innerWidth < 1280 ? 14 : 16} 
                className={refreshingScreen === screen.id ? 'spin' : ''}
                style={{ 
                  color: refreshingScreen === screen.id ? '#50E3C2' : undefined,
                  animation: refreshingScreen === screen.id ? 'spin 1s linear infinite' : undefined
                }}
              />
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => window.open(screen.display_url, '_blank')}
              data-testid={`view-screen-${screen.id}`}
              title={t('customerScreens.previewScreen')}
              style={{ padding: 'clamp(0.4rem, 1vw, 0.5rem)' }}
            >
              <Eye size={window.innerWidth < 1280 ? 14 : 16} />
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handleEdit(screen)}
              data-testid={`edit-screen-${screen.id}`}
              title={t('customerScreens.editScreen')}
              style={{ padding: 'clamp(0.4rem, 1vw, 0.5rem)' }}
            >
              <Edit2 size={window.innerWidth < 1280 ? 14 : 16} />
            </button>
            <button
              className="btn btn-danger"
              onClick={() => handleDelete(screen.id)}
              data-testid={`delete-screen-${screen.id}`}
              title={t('customerScreens.deleteScreen')}
              style={{ padding: 'clamp(0.4rem, 1vw, 0.5rem)' }}
            >
              <Trash2 size={window.innerWidth < 1280 ? 14 : 16} />
            </button>
          </>
        )}
      />

      {showModal && (
        <>
          <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingScreen(null); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingScreen ? t('customerScreens.editScreenTitle') : t('customerScreens.newScreenTitle')}</h2>
            <form onSubmit={editingScreen ? handleUpdate : handleCreate}>
              <div className="form-group">
                <label>{t('customerScreens.formScreenName')}</label>
                <input
                  type="text"
                  value={formData.screen_name}
                  onChange={(e) => setFormData({...formData, screen_name: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>{t('customerScreens.formOrientation')}</label>
                <select
                  value={formData.orientation}
                  onChange={(e) => setFormData({...formData, orientation: e.target.value})}
                >
                  <option value="landscape">{t('customerScreens.landscape')}</option>
                  <option value="portrait">{t('customerScreens.portrait')}</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" className="btn btn-primary">
                  {editingScreen ? t('customerScreens.update') : t('customerScreens.create')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setEditingScreen(null); }}>
                  {t('customerScreens.cancel')}
                </button>
              </div>
            </form>
            </div>
          </div>
        </>
      )}

      {/* Devices Modal */}
      {showDevicesModal && (
        <div className="modal-overlay" onClick={() => setShowDevicesModal(false)}>
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '900px', maxHeight: '80vh', overflow: 'auto' }}
          >
            <h2>Aktive enheter</h2>
            {loadingDevices ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p>Laster enheter...</p>
              </div>
            ) : selectedScreenDevices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', opacity: 0.7 }}>
                <p>Ingen enheter registrert for denne skjermen ennå.</p>
                <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  Enheter vil dukke opp her når skjermen åpnes i en nettleser.
                </p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ opacity: 0.8, color: '#FFFFFF' }}>
                    Viser {selectedScreenDevices.length} {selectedScreenDevices.length === 1 ? 'enhet' : 'enheter'}
                    {' '}({selectedScreenDevices.filter(d => d.is_online).length} online)
                  </p>
                </div>
                
                <table className="data-table" style={{ fontSize: '0.9rem' }}>
                  <thead>
                    <tr>
                      <th>{t('customerScreens.deviceTableStatus')}</th>
                      <th>{t('customerScreens.deviceTableIP')}</th>
                      <th>{t('customerScreens.deviceTableResolution')}</th>
                      <th>{t('customerScreens.deviceTableBrowser')}</th>
                      <th>{t('customerScreens.deviceTableOS')}</th>
                      <th>{t('customerScreens.deviceTableFirstSeen')}</th>
                      <th>{t('customerScreens.deviceTableLastSeen')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedScreenDevices.map((device, idx) => (
                      <tr key={idx} style={{ opacity: device.is_online ? 1 : 0.6 }}>
                        <td>
                          <span 
                            className={device.is_online ? 'badge badge-success' : 'badge badge-secondary'}
                            style={{ fontSize: '0.75rem' }}
                          >
                            {device.is_online ? t('customerScreens.online') : t('customerScreens.offline')}
                          </span>
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#FFFFFF' }}>
                          {device.ip_address}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#FFFFFF' }}>
                          {device.screen_resolution}
                        </td>
                        <td style={{ color: '#FFFFFF' }}>{device.browser}</td>
                        <td style={{ color: '#FFFFFF' }}>{device.os}</td>
                        <td style={{ fontSize: '0.85rem', color: '#FFFFFF' }}>
                          {new Date(device.first_seen).toLocaleString('no-NO', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td style={{ fontSize: '0.85rem', color: '#FFFFFF' }}>
                          {new Date(device.last_seen).toLocaleString('no-NO', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                <div style={{ 
                  marginTop: '1.5rem', 
                  padding: '1rem', 
                  background: 'rgba(80, 227, 194, 0.1)', 
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  color: '#FFFFFF'
                }}>
                  <p style={{ margin: 0, opacity: 0.9 }}>
                    💡 <strong>Tips:</strong> Hver unik kombinasjon av IP-adresse, nettleser og oppløsning 
                    regnes som en separat enhet. Dette sikrer nøyaktig sporing av antall fysiske skjermer i bruk.
                  </p>
                </div>
              </>
            )}
            
            <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowDevicesModal(false)}
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}
    </CustomerLayout>
  );
};

export default CustomerScreens;
